import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Plus, PencilSimple, Trash, Car, User, Upload, Download, Spinner, FileText, Archive, ArrowCounterClockwise } from '@phosphor-icons/react';
import axios from '../../utils/axiosInstance';
import { WlasnoscFilter, WlasnoscBadge, normalizeWlasnosc } from '../shared/WlasnoscFilter';

const API = `${window.location.origin}/api`;

const emptyForm = {
  nazwa: '',
  marka: '',
  model: '',
  nr_rejestracyjny: '',
  vin: '',
  karta_paliwowa: '',
  employee_id: '',
  typ: 'osobowy',
  wlasnosc: 'firmowy',
};

export default function PojazdyListTab({ readOnly = false } = {}) {
  const [pojazdy, setPojazdy] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrMessage, setOcrMessage] = useState('');
  const [pendingDocument, setPendingDocument] = useState(null); // file selected before save

  const [formData, setFormData] = useState(emptyForm);
  const [tempDocumentToken, setTempDocumentToken] = useState(null);
  const fileInputRef = useRef(null);
  const [wlasnoscFilter, setWlasnoscFilter] = useState('all');
  const [viewMode, setViewMode] = useState('active'); // 'active' | 'archived'
  const [archivedList, setArchivedList] = useState([]);

  // Filtered view + per-tab counts
  const sourceList = viewMode === 'active' ? pojazdy : archivedList;
  const filteredPojazdy = React.useMemo(() => {
    if (wlasnoscFilter === 'all') return sourceList;
    return sourceList.filter((p) => normalizeWlasnosc(p.wlasnosc) === wlasnoscFilter);
  }, [sourceList, wlasnoscFilter]);

  const wlasnoscCounts = React.useMemo(() => {
    const c = { all: sourceList.length, firmowy: 0, prywatny: 0 };
    for (const p of sourceList) c[normalizeWlasnosc(p.wlasnosc)]++;
    return c;
  }, [sourceList]);

  useEffect(() => {
    fetchPojazdy();
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees`);
      setEmployees(response.data);
    } catch (err) {
      console.error('Error fetching employees:', err);
    }
  };

  const fetchPojazdy = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/pojazdy`);
      setPojazdy(response.data);
    } catch (err) {
      console.error('Error fetching pojazdy:', err);
      alert('Błąd podczas pobierania danych');
    } finally {
      setLoading(false);
    }
  };

  const fetchArchived = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/pojazdy`, { params: { archived_only: true } });
      setArchivedList(response.data);
    } catch (err) {
      console.error('Error fetching archived pojazdy:', err);
    }
  }, []);

  useEffect(() => {
    if (viewMode === 'archived') fetchArchived();
  }, [viewMode, fetchArchived]);

  const handleArchive = async (pojazd) => {
    if (!window.confirm(`Czy na pewno chcesz przenieść pojazd "${pojazd.nazwa}" do archiwum?\n\nHistoria napraw, terminów i przebiegów pozostanie zachowana.`)) return;
    try {
      await axios.post(`${API}/pojazdy/${pojazd.id}/archive`);
      fetchPojazdy();
      fetchArchived();
    } catch (err) {
      alert(err.response?.data?.detail || 'Błąd podczas archiwizacji');
    }
  };

  const handleUnarchive = async (pojazd) => {
    try {
      await axios.post(`${API}/pojazdy/${pojazd.id}/unarchive`);
      fetchPojazdy();
      fetchArchived();
    } catch (err) {
      alert(err.response?.data?.detail || 'Błąd podczas przywracania');
    }
  };

  const resetForm = () => {
    setFormData(emptyForm);
    setShowForm(false);
    setEditingId(null);
    setPendingDocument(null);
    setTempDocumentToken(null);
    setOcrMessage('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const uploadDocumentForPojazd = async (pojazdId, file, docType = 'dowod') => {
    const fd = new FormData();
    fd.append('file', file);
    const response = await axios.post(
      `${API}/pojazdy/${pojazdId}/upload-document?type=${docType}`,
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  };

  const handleUbezpieczenieUpload = async (event, pojazdId) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!pojazdId) {
      alert('Najpierw zapisz pojazd, potem dodaj polisę.');
      event.target.value = '';
      return;
    }
    try {
      const result = await uploadDocumentForPojazd(pojazdId, file, 'ubezpieczenie');
      const dateMsg = result.ubezpieczenie_data_konca
        ? `\n\nWydobyta data końca ubezpieczenia: ${result.ubezpieczenie_data_konca}\n(zaktualizowana w Terminach)`
        : '\n\nNie udało się automatycznie wydobyć daty końca — uzupełnij ręcznie w Terminach.';
      alert((result.message || 'Polisa zapisana') + dateMsg);
      fetchPojazdy();
    } catch (err) {
      console.error('Ubezpieczenie upload failed:', err);
      const msg = err.response?.data?.detail || 'Błąd podczas przesyłania polisy';
      alert(msg);
    } finally {
      event.target.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData };
      if (!editingId && tempDocumentToken) {
        payload.temp_document_token = tempDocumentToken;
      }

      let pojazdId = editingId;
      if (editingId) {
        await axios.put(`${API}/pojazdy/${editingId}`, formData);
      } else {
        const res = await axios.post(`${API}/pojazdy`, payload);
        pojazdId = res.data.id;
      }

      resetForm();
      fetchPojazdy();
    } catch (err) {
      console.error('Error saving pojazd:', err);
      const errorMsg = err.response?.data?.detail || 'Błąd podczas zapisywania';
      alert(errorMsg);
    }
  };

  const handleEdit = (pojazd) => {
    setFormData({
      nazwa: pojazd.nazwa || '',
      marka: pojazd.marka || '',
      model: pojazd.model || '',
      nr_rejestracyjny: pojazd.nr_rejestracyjny || '',
      vin: pojazd.vin || '',
      karta_paliwowa: pojazd.karta_paliwowa || '',
      employee_id: pojazd.employee_id || '',
      typ: pojazd.typ || 'osobowy',
      wlasnosc: normalizeWlasnosc(pojazd.wlasnosc),
    });
    setEditingId(pojazd.id);
    setShowForm(true);
    setPendingDocument(null);
    setOcrMessage('');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Czy na pewno chcesz usunąć ten pojazd?')) return;
    try {
      await axios.delete(`${API}/pojazdy/${id}`);
      fetchPojazdy();
    } catch (err) {
      console.error('Error deleting pojazd:', err);
      const errorMsg = err.response?.data?.detail || 'Błąd podczas usuwania';
      alert(errorMsg);
    }
  };

  // Triggered when user picks a file on the form's "dodaj dowód rejestracyjny" input
  const handleDocumentSelected = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setOcrMessage('');

    // EDITING existing vehicle: upload directly + run OCR + persist
    if (editingId) {
      setOcrLoading(true);
      try {
        const data = await uploadDocumentForPojazd(editingId, file);
        const extracted = data.extracted_data || {};
        setFormData((prev) => ({
          ...prev,
          marka: extracted.marka || prev.marka,
          model: extracted.model || prev.model,
          nr_rejestracyjny: extracted.nr_rejestracyjny || prev.nr_rejestracyjny,
          vin: extracted.vin || prev.vin,
        }));
        setOcrMessage(data.message || 'Dane wydobyte z dokumentu');
        fetchPojazdy();
      } catch (err) {
        console.error('OCR upload failed:', err);
        const msg = err.response?.data?.detail || 'Błąd podczas przesyłania dokumentu';
        setOcrMessage(msg);
        alert(msg);
      } finally {
        setOcrLoading(false);
      }
      return;
    }

    // NEW vehicle: run OCR-only via /extract-document, prefill fields,
    // store temp_document_token to attach the file on submit.
    setOcrLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/pojazdy/extract-document`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const extracted = res.data?.extracted_data || {};
      setTempDocumentToken(res.data?.temp_document_token || null);
      setPendingDocument(file);
      setFormData((prev) => ({
        ...prev,
        marka: extracted.marka || prev.marka,
        model: extracted.model || prev.model,
        nr_rejestracyjny: extracted.nr_rejestracyjny || prev.nr_rejestracyjny,
        vin: extracted.vin || prev.vin,
        nazwa:
          prev.nazwa ||
          [extracted.marka, extracted.model].filter(Boolean).join(' ') ||
          prev.nazwa,
      }));
      const anyExtracted = Object.values(extracted).some((v) => v);
      setOcrMessage(
        anyExtracted
          ? 'Dane wydobyte z dokumentu. Sprawdź i kliknij "Dodaj pojazd", aby zapisać.'
          : 'Nie udało się wydobyć danych — uzupełnij ręcznie i zapisz.'
      );
    } catch (err) {
      console.error('OCR extract failed:', err);
      const msg = err.response?.data?.detail || 'Błąd podczas przetwarzania dokumentu';
      setOcrMessage(msg);
      alert(msg);
    } finally {
      setOcrLoading(false);
    }
  };

  const handleDownloadDocument = async (pojazd, docType = 'dowod') => {
    try {
      const response = await axios.get(
        `${API}/pojazdy/${pojazd.id}/document?type=${docType}`,
        { responseType: 'blob' }
      );
      const contentType =
        response.headers?.['content-type'] ||
        response.data?.type ||
        'application/octet-stream';
      const extMap = {
        'application/pdf': 'pdf',
        'image/jpeg': 'jpg',
        'image/jpg': 'jpg',
        'image/png': 'png',
      };
      const ext = extMap[contentType.split(';')[0].trim()] || 'bin';
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const prefix = docType === 'ubezpieczenie' ? 'polisa' : 'dowod';
      a.download = `${prefix}_${pojazd.nr_rejestracyjny || pojazd.nazwa || pojazd.id}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
      alert('Nie udało się pobrać dokumentu');
    }
  };

  if (loading) {
    return <div className="text-center py-12" data-testid="pojazdy-loading">Ładowanie...</div>;
  }

  return (
    <div className="max-w-[1920px] mx-auto px-6 py-6 space-y-6" data-testid="pojazdy-list-tab">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h2 className="text-3xl font-bold text-slate-800">
            {viewMode === 'active' ? 'Lista pojazdów' : 'Archiwum pojazdów'}
          </h2>
          <p className="text-slate-600 mt-1">
            {viewMode === 'active' ? 'Zarządzanie flotą pojazdów' : 'Sprzedane / wycofane pojazdy — historia zachowana'}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="inline-flex bg-slate-100 rounded-md p-0.5" role="tablist">
            <button
              onClick={() => setViewMode('active')}
              className={`px-3 py-1.5 text-sm font-medium rounded ${viewMode === 'active' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
              data-testid="pojazdy-view-active"
            >
              Aktywne ({pojazdy.length})
            </button>
            <button
              onClick={() => setViewMode('archived')}
              className={`px-3 py-1.5 text-sm font-medium rounded flex items-center gap-1 ${viewMode === 'archived' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
              data-testid="pojazdy-view-archived"
            >
              <Archive className="w-3.5 h-3.5" /> Archiwum ({archivedList.length})
            </button>
          </div>
          <WlasnoscFilter
            value={wlasnoscFilter}
            onChange={setWlasnoscFilter}
            counts={wlasnoscCounts}
          />
          {!readOnly && viewMode === 'active' && (
          <button
            data-testid="add-pojazd-btn"
            onClick={() => {
              if (showForm) {
                resetForm();
              } else {
                setFormData(emptyForm);
                setShowForm(true);
              }
            }}
            className="px-5 py-3 bg-muted hover:from-green-700 hover:to-emerald-800 text-white rounded-xl font-semibold flex items-center gap-2 shadow-lg hover:border border-border transition-all"
          >
            <Plus className="w-5 h-5" weight="bold" />
            Dodaj pojazd
          </button>
          )}
        </div>
      </div>

      {/* Add/Edit Form */}
      {!readOnly && showForm && (
        <div className="bg-muted rounded-sm p-6 shadow-lg border border-green-200" data-testid="pojazd-form">
          <h3 className="text-xl font-bold text-slate-800 mb-4">
            {editingId ? 'Edytuj pojazd' : 'Dodaj nowy pojazd'}
          </h3>

          {/* OCR Upload Area */}
          <div className="bg-white border-2 border-dashed border-border rounded-xl p-5 mb-5">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h4 className="font-semibold text-slate-800 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-foreground" weight="bold" />
                  Dowód rejestracyjny (OCR)
                </h4>
                <p className="text-sm text-slate-600 mt-1">
                  Wgraj zdjęcie lub PDF dowodu — dane (marka, model, nr rej., VIN) wypełnią się automatycznie.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <input
                  ref={fileInputRef}
                  data-testid="pojazd-document-input"
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,application/pdf"
                  onChange={handleDocumentSelected}
                  className="hidden"
                  id="pojazd-document-file"
                />
                <label
                  htmlFor="pojazd-document-file"
                  data-testid="pojazd-upload-document-btn"
                  className={`px-4 py-2.5 rounded-lg font-semibold flex items-center gap-2 cursor-pointer transition-all ${
                    ocrLoading
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-foreground hover:bg-black text-white shadow-md'
                  }`}
                >
                  {ocrLoading ? (
                    <>
                      <Spinner className="w-5 h-5 animate-spin" weight="bold" />
                      Przetwarzanie…
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5" weight="bold" />
                      Dodaj dowód rejestracyjny
                    </>
                  )}
                </label>
              </div>
            </div>
            {ocrMessage && (
              <p className="text-sm text-foreground mt-3" data-testid="pojazd-ocr-message">
                {ocrMessage}
              </p>
            )}
          </div>

          {/* Ubezpieczenie Upload (only when editing — needs existing pojazd_id) */}
          <div className="bg-white border-2 border-dashed border-amber-300 rounded-xl p-5 mb-5">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h4 className="font-semibold text-slate-800 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-amber-600" weight="bold" />
                  Polisa ubezpieczeniowa (OCR)
                </h4>
                <p className="text-sm text-slate-600 mt-1">
                  {editingId
                    ? 'Wgraj zdjęcie lub PDF polisy. Data końca ubezpieczenia zostanie automatycznie zapisana w Terminach. Plik będzie dostępny dla przypisanego pracownika.'
                    : 'Najpierw zapisz pojazd, potem wróć do edycji, aby dodać polisę.'}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,application/pdf"
                  onChange={(e) => handleUbezpieczenieUpload(e, editingId)}
                  className="hidden"
                  id="pojazd-ubezpieczenie-file"
                  data-testid="pojazd-ubezpieczenie-input"
                  disabled={!editingId}
                />
                <label
                  htmlFor="pojazd-ubezpieczenie-file"
                  data-testid="pojazd-upload-ubezpieczenie-btn"
                  className={`px-4 py-2.5 rounded-lg font-semibold flex items-center gap-2 transition-all ${
                    !editingId
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-amber-600 hover:bg-amber-700 text-white shadow-md cursor-pointer'
                  }`}
                >
                  <Upload className="w-5 h-5" weight="bold" />
                  Dodaj polisę
                </label>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Nazwa pojazdu *
                </label>
                <input
                  data-testid="pojazd-nazwa-input"
                  type="text"
                  value={formData.nazwa}
                  onChange={(e) => setFormData({ ...formData, nazwa: e.target.value })}
                  required
                  placeholder="np. Bus A1, Auto 123"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Marka</label>
                <input
                  data-testid="pojazd-marka-input"
                  type="text"
                  value={formData.marka}
                  onChange={(e) => setFormData({ ...formData, marka: e.target.value })}
                  placeholder="np. Volkswagen"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Model</label>
                <input
                  data-testid="pojazd-model-input"
                  type="text"
                  value={formData.model}
                  onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                  placeholder="np. Transit"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Nr rejestracyjny
                </label>
                <input
                  data-testid="pojazd-nr-rejestracyjny-input"
                  type="text"
                  value={formData.nr_rejestracyjny}
                  onChange={(e) =>
                    setFormData({ ...formData, nr_rejestracyjny: e.target.value })
                  }
                  placeholder="np. WA12345"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">VIN</label>
                <input
                  data-testid="pojazd-vin-input"
                  type="text"
                  value={formData.vin}
                  onChange={(e) => setFormData({ ...formData, vin: e.target.value })}
                  placeholder="17 znaków"
                  maxLength={17}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent uppercase"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Karta paliwowa
                </label>
                <input
                  data-testid="pojazd-karta-paliwowa-input"
                  type="text"
                  value={formData.karta_paliwowa}
                  onChange={(e) => setFormData({ ...formData, karta_paliwowa: e.target.value })}
                  placeholder="np. 7028 1234 5678 9012"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Typ pojazdu
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: 'osobowy', label: 'Osobowy', desc: 'Pracownik wpisuje tankowania' },
                    { value: 'firmowy', label: 'Firmowy', desc: 'Pracownik NIE wpisuje (np. dostawczak)' },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, typ: opt.value })}
                      className={`px-3 py-2 rounded-lg border-2 text-left transition ${formData.typ === opt.value
                        ? 'border-green-500 bg-green-50'
                        : 'border-slate-200 bg-white hover:border-slate-300'}`}
                      data-testid={`pojazd-typ-${opt.value}`}
                    >
                      <div className="font-semibold text-sm">{opt.label}</div>
                      <div className="text-[10px] text-slate-500">{opt.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Własność
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: 'firmowy', label: 'Firmowy', desc: 'Auto należy do firmy', color: 'blue' },
                    { value: 'prywatny', label: 'Prywatny', desc: 'Auto pracownika', color: 'amber' },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, wlasnosc: opt.value })}
                      className={`px-3 py-2 rounded-lg border-2 text-left transition ${formData.wlasnosc === opt.value
                        ? (opt.color === 'blue' ? 'border-blue-500 bg-blue-50' : 'border-amber-500 bg-amber-50')
                        : 'border-slate-200 bg-white hover:border-slate-300'}`}
                      data-testid={`pojazd-wlasnosc-${opt.value}`}
                    >
                      <div className="font-semibold text-sm">{opt.label}</div>
                      <div className="text-[10px] text-slate-500">{opt.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Przypisany pracownik (opcjonalnie)
                </label>
                <select
                  data-testid="pojazd-employee-select"
                  value={formData.employee_id}
                  onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="">Brak przypisania</option>
                  {employees.map((emp) => (
                    <option key={emp._id} value={emp._id}>
                      {emp.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                data-testid="pojazd-submit-btn"
                type="submit"
                className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition-colors"
              >
                {editingId ? 'Zapisz zmiany' : 'Dodaj pojazd'}
              </button>
              <button
                data-testid="pojazd-cancel-btn"
                type="button"
                onClick={resetForm}
                className="px-6 py-3 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded-lg font-semibold transition-colors"
              >
                Anuluj
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Pojazdy Table */}
      <div className="bg-white rounded-sm shadow-lg border border-gray-100 overflow-hidden">
        {filteredPojazdy.length === 0 ? (
          <div className="text-center py-16">
            <Car className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">
              {pojazdy.length === 0
                ? 'Brak pojazdów w systemie'
                : `Brak pojazdów w filtrze "${wlasnoscFilter}"`}
            </p>
            <p className="text-gray-400 text-sm mt-2">
              {pojazdy.length === 0
                ? 'Kliknij "Dodaj pojazd" aby dodać pierwszy pojazd'
                : 'Zmień filtr powyżej lub dodaj nowy pojazd'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="pojazdy-table">
              <thead className="bg-muted">
                <tr>
                  <th className="text-left py-4 px-4 text-xs font-bold uppercase tracking-wider text-slate-600">
                    Nazwa
                  </th>
                  <th className="text-left py-4 px-4 text-xs font-bold uppercase tracking-wider text-slate-600">
                    Marka
                  </th>
                  <th className="text-left py-4 px-4 text-xs font-bold uppercase tracking-wider text-slate-600">
                    Model
                  </th>
                  <th className="text-left py-4 px-4 text-xs font-bold uppercase tracking-wider text-slate-600">
                    Nr rej.
                  </th>
                  <th className="text-left py-4 px-4 text-xs font-bold uppercase tracking-wider text-slate-600">
                    VIN
                  </th>
                  <th className="text-left py-4 px-4 text-xs font-bold uppercase tracking-wider text-slate-600">
                    Karta paliwowa
                  </th>
                  <th className="text-left py-4 px-4 text-xs font-bold uppercase tracking-wider text-slate-600">
                    Pracownik
                  </th>
                  <th className="text-center py-4 px-4 text-xs font-bold uppercase tracking-wider text-slate-600">
                    Dowód
                  </th>
                  <th className="text-center py-4 px-4 text-xs font-bold uppercase tracking-wider text-slate-600">
                    Polisa
                  </th>
                  <th className="text-right py-4 px-4 text-xs font-bold uppercase tracking-wider text-slate-600">
                    Akcje
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredPojazdy.map((pojazd, idx) => (
                  <tr
                    key={pojazd.id}
                    className={`hover:bg-green-50 transition-colors duration-200 ${
                      idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'
                    }`}
                    data-testid={`pojazd-row-${pojazd.id}`}
                  >
                    <td className="py-4 px-4 border-b border-gray-100">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-muted rounded-lg flex items-center justify-center shrink-0">
                          <Car className="w-5 h-5 text-white" weight="bold" />
                        </div>
                        <div className="flex flex-col gap-0.5 min-w-0">
                          <span className="font-bold text-slate-800 truncate">{pojazd.nazwa}</span>
                          <WlasnoscBadge value={pojazd.wlasnosc} size="xs" />
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-4 border-b border-gray-100 text-slate-700">
                      {pojazd.marka || <span className="text-gray-400">—</span>}
                    </td>
                    <td className="py-4 px-4 border-b border-gray-100 text-slate-700">
                      {pojazd.model || <span className="text-gray-400">—</span>}
                    </td>
                    <td className="py-4 px-4 border-b border-gray-100 font-mono text-slate-700">
                      {pojazd.nr_rejestracyjny || <span className="text-gray-400 font-sans">—</span>}
                    </td>
                    <td className="py-4 px-4 border-b border-gray-100 font-mono text-xs text-slate-700">
                      {pojazd.vin || <span className="text-gray-400 font-sans">—</span>}
                    </td>
                    <td className="py-4 px-4 border-b border-gray-100 font-mono text-xs text-slate-700">
                      {pojazd.karta_paliwowa || <span className="text-gray-400 font-sans">—</span>}
                    </td>
                    <td className="py-4 px-4 border-b border-gray-100">
                      {pojazd.employee_name ? (
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 bg-blue-500 rounded-full flex items-center justify-center shrink-0">
                            <User className="w-4 h-4 text-white" weight="bold" />
                          </div>
                          <span className="font-semibold text-slate-700">
                            {pojazd.employee_name}
                          </span>
                        </div>
                      ) : (
                        <span className="text-gray-400 italic">Brak</span>
                      )}
                    </td>
                    <td className="py-4 px-4 border-b border-gray-100 text-center">
                      {pojazd.dokument_url ? (
                        <button
                          data-testid={`pojazd-download-doc-${pojazd.id}`}
                          onClick={() => handleDownloadDocument(pojazd, 'dowod')}
                          className="p-2 hover:bg-muted rounded-lg transition-colors text-foreground inline-flex items-center gap-1"
                          title="Pobierz dowód rejestracyjny"
                        >
                          <Download className="w-5 h-5" weight="bold" />
                        </button>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="py-4 px-4 border-b border-gray-100 text-center">
                      {pojazd.ubezpieczenie_url ? (
                        <button
                          data-testid={`pojazd-download-ub-${pojazd.id}`}
                          onClick={() => handleDownloadDocument(pojazd, 'ubezpieczenie')}
                          className="p-2 hover:bg-amber-100 rounded-lg transition-colors text-amber-600 inline-flex items-center gap-1"
                          title="Pobierz polisę"
                        >
                          <Download className="w-5 h-5" weight="bold" />
                        </button>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="py-4 px-4 border-b border-gray-100 text-right">
                      {readOnly ? (
                        <span className="text-gray-300 text-xs">—</span>
                      ) : viewMode === 'active' ? (
                        <div className="flex items-center justify-end gap-2">
                          <button
                            data-testid={`pojazd-edit-btn-${pojazd.id}`}
                            onClick={() => handleEdit(pojazd)}
                            className="p-2 hover:bg-muted rounded-lg transition-colors text-foreground"
                            title="Edytuj"
                          >
                            <PencilSimple className="w-5 h-5" weight="bold" />
                          </button>
                          <button
                            data-testid={`pojazd-archive-btn-${pojazd.id}`}
                            onClick={() => handleArchive(pojazd)}
                            className="p-2 hover:bg-amber-100 rounded-lg transition-colors text-amber-600"
                            title="Do archiwum"
                          >
                            <Archive className="w-5 h-5" weight="bold" />
                          </button>
                          <button
                            data-testid={`pojazd-delete-btn-${pojazd.id}`}
                            onClick={() => handleDelete(pojazd.id)}
                            className="p-2 hover:bg-red-100 rounded-lg transition-colors text-red-600"
                            title="Usuń"
                          >
                            <Trash className="w-5 h-5" weight="bold" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-end gap-2">
                          <button
                            data-testid={`pojazd-unarchive-btn-${pojazd.id}`}
                            onClick={() => handleUnarchive(pojazd)}
                            className="p-2 hover:bg-emerald-100 rounded-lg transition-colors text-emerald-600"
                            title="Przywróć"
                          >
                            <ArrowCounterClockwise className="w-5 h-5" weight="bold" />
                          </button>
                          <button
                            data-testid={`pojazd-delete-btn-${pojazd.id}`}
                            onClick={() => handleDelete(pojazd.id)}
                            className="p-2 hover:bg-red-100 rounded-lg transition-colors text-red-600"
                            title="Usuń trwale"
                          >
                            <Trash className="w-5 h-5" weight="bold" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Summary */}
      {pojazdy.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-muted rounded-sm p-6 text-white shadow-lg">
            <div className="flex items-center gap-3 mb-2">
              <Car className="w-8 h-8" weight="bold" />
              <h3 className="text-lg font-semibold">Łącznie pojazdów</h3>
            </div>
            <p className="text-4xl font-bold">{pojazdy.length}</p>
          </div>

          <div className="bg-foreground rounded-sm p-6 text-white shadow-lg">
            <div className="flex items-center gap-3 mb-2">
              <User className="w-8 h-8" weight="bold" />
              <h3 className="text-lg font-semibold">Przypisane</h3>
            </div>
            <p className="text-4xl font-bold">
              {pojazdy.filter((p) => p.employee_id).length}
            </p>
          </div>

          <div className="bg-muted rounded-sm p-6 text-white shadow-lg">
            <div className="flex items-center gap-3 mb-2">
              <Car className="w-8 h-8" weight="bold" />
              <h3 className="text-lg font-semibold">Bez przypisania</h3>
            </div>
            <p className="text-4xl font-bold">
              {pojazdy.filter((p) => !p.employee_id).length}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
